import url from '@/utils/config';
import React from 'react';
import LinkButton from '@/components/atoms/LinkButton';
import { useRecoilValue } from 'recoil';
import { DeliveryInfoState } from '@/states/deviceDeliCollInfoState';
import { formatDateStringToJapanese } from '@/utils/commonFunc';
import { Constants } from '@/utils/constants';
import Heading from '../atoms/Heading';

type Prop = {
  isOpen: boolean;
  onClose: () => void;
};

/**
 * 機器配送の希望日と追跡番号をモーダルで確認し、変更可能な場合は再配送導線も出すポップアップ。
 * 配送状態は問い合わせが起きやすいため、追跡リンクと変更可否を同じ場所にまとめている。
 */
const DeliveryPopup = ({ isOpen, onClose }: Prop) => {
  const delivery = useRecoilValue(DeliveryInfoState);

  const contactNumber = (trackNo: string, deliveryTraderUrl: string) => {
    if (trackNo) {
      // 照会ページ URL が無い配送会社もあるため、その場合は番号だけを表示する。
      if (deliveryTraderUrl) {
        return (
          <a href={deliveryTraderUrl} target='_blank' rel='noreferrer'>
            {trackNo}
          </a>
        );
      }
      return trackNo;
    }
    return <span>-</span>;
  };

  if (isOpen) {
    if (typeof window === 'object') {
      document.body.style.overflow = 'hidden';
    }
    return (
      <>
        <div className='m-modal js-modal is-modal-fade is-modal-show' id='modal-use-01'>
          <div className='m-modal__inner'>
            <div className='m-modal__content'>
              <Heading as='h2'>配送情報</Heading>
              <Heading as='h3'>配送情報</Heading>
              <div className='m-table' style={{ marginLeft: '30px', marginRight: '30px' }}>
                <table>
                  <colgroup>
                    <col className='w-50' />
                    <col className='w-50' />
                    <col />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className='m-table__th'>配送希望日</th>
                      <th className='m-table__th'>問い合わせ番号</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className='m-table__td' style={{ overflowWrap: 'break-word' }}>
                        {/* 未確定の配送は空値で返るため、確定前でも UI が崩れないようにする。 */}
                        {delivery.delivery_pfd_date === '' ? (
                          <span>-</span>
                        ) : (
                          formatDateStringToJapanese(delivery.delivery_pfd_date)
                        )}
                      </td>
                      <td className='m-table__td' style={{ overflowWrap: 'break-word' }}>
                        {/* 問い合わせ番号は配送会社 URL の有無に応じてリンク化有無を切り替える。 */}
                        {contactNumber(delivery.track_no, delivery.delivery_trader_url)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className='m-button align-center'>
                {delivery.change_pfd_flg !== '0' && (
                  // 変更可否フラグが立っている配送だけ、変更・再配送導線を表示する。
                  <LinkButton variant='secondary' href='/top/delivery/change_delivery/'>
                    {delivery.redelivery_flg === '0'
                      ? Constants.DeliCollInfo.Delivery.DELIVERY_BUTTON
                      : Constants.DeliCollInfo.Delivery.REDELIVERY_BUTTON}
                  </LinkButton>
                )}
              </div>
            </div>
            <button
              type='button'
              onClick={onClose}
              className='m-modal__closeButton js-modal-coseButton'
            >
              <img
                className='m-modal__closeButtonImage'
                src={url('/images/icon_close.svg')}
                alt='閉じる'
                width='40'
                height='40'
              />
            </button>
          </div>
        </div>
        <span
          role='button'
          tabIndex={0}
          className='fn-overlay is-overlay-show'
          onClick={onClose}
          onKeyDown={onClose}
          aria-label='閉じる'
          data-testid='show'
        />
      </>
    );
  }
  if (typeof window === 'object') {
    document.body.style.overflow = '';
  }
  return <span className='fn-overlay' data-testid='hidden' />;
};

export default DeliveryPopup;
